import { useMemo, useState } from "react";
import { Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { useBotFunnel, useBotRules, useSaveBotRules } from "@/hooks/useBotDetection";
import { APP_LOCALE, moneyFormatter } from "@/config/currency";
import { botFunnel, parsePatterns, ruleChanged, samePatterns, type BotFunnel, type BotRule } from "@/lib/bot-funnel";
import { cn } from "@/lib/utils";
import { BotFunnelView } from "./BotFunnelView";

/**
 * Settings → Bot Detection. The rules live in public.bot_rules and apply when
 * data is read (migration 20261019170000): switching a rule changes every
 * Clicks number at once, history included. The funnel (BotFunnelView) has a
 * switch in each rule's row and moves before Save; every
 * rule's full settings - description, list - are in the Rules section below
 * it; the bar at the bottom says in numbers what the unsaved changes do.
 */

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

const int = new Intl.NumberFormat(APP_LOCALE);
const euro = moneyFormatter({ maximumFractionDigits: 0 });
/** "+1,234" / "−1,234" / "±0". */
const signed = (n: number, f: (v: number) => string) => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${f(Math.abs(n))}`;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const ddmmyyyy = (iso: string) => iso.split("-").reverse().join(".");

/** The last `days` days up to today, in UTC like clicks_daily.click_date. */
const periodRange = (days: number, now = new Date()) => {
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(now) };
};

type Draft = Record<string, Pick<BotRule, "enabled" | "patterns">>;

/**
 * A rule's list as one text field, one entry per line.
 * Keeps its own text so a blank line survives typing; the
 * parent remounts it (key) after Save / Discard.
 */
function PatternsEditor({ rule, onChange }: { rule: BotRule; onChange: (patterns: string[]) => void }) {
  const [text, setText] = useState(rule.patterns.join("\n"));
  const update = (v: string) => {
    setText(v);
    onChange(parsePatterns(v));
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <span>One per line</span>
        <span className="tabular-nums">{rule.patterns.length} {rule.patterns.length === 1 ? "entry" : "entries"}</span>
      </div>
      <textarea
        value={text}
        onChange={(e) => update(e.target.value)}
        rows={Math.min(Math.max(rule.patterns.length + 1, 3), 10)}
        spellCheck={false}
        aria-label={`${rule.label} list`}
        placeholder={rule.key === "user_agent" ? "e.g. curl" : "e.g. hetzner"}
        className="w-full resize-y rounded-md border border-border bg-transparent p-2 font-mono text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
      />
      <p className="text-xs text-muted-foreground">
        Case-insensitive “contains”. List changes apply to new clicks from the next hourly refresh; older days
        once they are recomputed.
      </p>
    </div>
  );
}

/** What the rule does in the period: cuts (on) or would cut (off). */
function RuleImpact({ rule, funnel }: { rule: BotRule; funnel: BotFunnel | undefined }) {
  if (!funnel) return null;
  const stage = funnel.stages.find((s) => s.rule.key === rule.key);
  if (!stage) return null;
  const t = rule.enabled ? stage.cut : stage.only;
  const costsSales = t.sales > 0 || t.revenue > 0;
  return (
    <span className="flex flex-wrap items-center justify-end gap-2">
      {funnel.legacy.clicks > 0 ? (
        <span className="signal-text-warning text-xs">
          not computed on {((funnel.legacy.clicks / funnel.total.clicks) * 100).toFixed(0)}% of clicks yet
        </span>
      ) : null}
      <span
        className={cn(
          "rounded-full px-2 py-0.5 text-xs tabular-nums",
          rule.enabled ? "bg-primary/10 text-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {rule.enabled ? "cuts" : "would cut"} {int.format(t.clicks)} clicks ·{" "}
        <span className={costsSales ? "signal-text-negative font-medium" : ""}>
          {t.sales} {t.sales === 1 ? "sale" : "sales"}
          {t.revenue > 0 ? ` · ${euro.format(t.revenue)}` : ""}
        </span>
      </span>
    </span>
  );
}

export function BotDetectionSettings() {
  const [period, setPeriod] = useState<Period>(30);
  const { from, to } = useMemo(() => periodRange(period), [period]);
  const rulesQuery = useBotRules();
  const funnelQuery = useBotFunnel(from, to);
  const save = useSaveBotRules();
  const [draft, setDraft] = useState<Draft>({});

  const saved = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const rules = useMemo(() => saved.map((r) => ({ ...r, ...draft[r.key] })), [saved, draft]);
  const changed = rules.filter((r) => ruleChanged(r, saved.find((x) => x.key === r.key)));
  const funnel = useMemo(
    () => (funnelQuery.data ? botFunnel(funnelQuery.data, rules) : undefined),
    [funnelQuery.data, rules],
  );
  const savedFunnel = useMemo(
    () => (funnelQuery.data ? botFunnel(funnelQuery.data, saved) : undefined),
    [funnelQuery.data, saved],
  );
  // The switch flipped last: its row flashes, and the bar says what it alone did.
  const [lastToggle, setLastToggle] = useState<{ key: string; seq: number } | null>(null);

  // Bumped on Save / Discard: remounts the list fields onto the saved lists.
  const [reset, setReset] = useState(0);
  const discard = () => {
    setDraft({});
    setReset((n) => n + 1);
    setLastToggle(null);
  };
  const edit = (rule: BotRule, values: Partial<Pick<BotRule, "enabled" | "patterns">>) =>
    setDraft((d) => ({ ...d, [rule.key]: { enabled: rule.enabled, patterns: rule.patterns, ...values } }));
  const toggle = (rule: BotRule, enabled: boolean) => {
    edit(rule, { enabled });
    setLastToggle((t) => ({ key: rule.key, seq: (t?.seq ?? 0) + 1 }));
  };
  // What the last switch alone did to the clean traffic: the funnel with it flipped back.
  const lastRule = lastToggle && changed.find((r) => r.key === lastToggle.key && r.enabled !== saved.find((x) => x.key === r.key)?.enabled);
  const back =
    lastRule && funnelQuery.data
      ? botFunnel(funnelQuery.data, rules.map((r) => (r.key === lastRule.key ? { ...r, enabled: !r.enabled } : r))).clean
      : undefined;
  const lastNet =
    back && funnel
      ? { clicks: funnel.clean.clicks - back.clicks, sales: funnel.clean.sales - back.sales, revenue: funnel.clean.revenue - back.revenue }
      : undefined;
  // User-agent / ISP lists change bits at the hourly refresh: nothing to preview.
  const listEdited = changed.some(
    (r) => !samePatterns(r.patterns, saved.find((x) => x.key === r.key)?.patterns ?? []),
  );
  const ruleSettings = (rule: BotRule) => (
    <div key={rule.key} className="flex gap-4 py-4">
      <Switch checked={rule.enabled} onCheckedChange={(v) => toggle(rule, v)} aria-label={rule.label} className="mt-0.5" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium">{rule.label}</span>
          <RuleImpact rule={rule} funnel={funnel} />
        </div>
        <p className="text-xs text-muted-foreground">{rule.description}</p>
        {rule.editable ? (
          <PatternsEditor key={`${rule.key}-${reset}`} rule={rule} onChange={(patterns) => edit(rule, { patterns })} />
        ) : null}
      </div>
    </div>
  );

  const onSave = () =>
    save.mutate(changed, {
      onSuccess: () => {
        discard();
        toast.success("Bot rules saved: every Clicks number follows them now");
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save the bot rules"),
    });

  const lastChanged = saved.reduce<string | undefined>(
    (max, r) => (r.updated_at && (!max || r.updated_at > max) ? r.updated_at : max),
    undefined,
  );

  return (
    <div className="max-w-6xl space-y-6 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bot className="size-4 text-muted-foreground" />
            <h2 className="font-semibold">Bot detection</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Switch a rule and watch the funnel: how many clicks it cuts, and the sales and revenue it hides.
            These are the global rules: Clicks → Bots → Global rules hides by them, and anyone can save their
            own preset there.
            {lastChanged ? ` Rules last saved ${ddmmyyyy(lastChanged.slice(0, 10))} ${lastChanged.slice(11, 16)} UTC.` : ""}
          </p>
        </div>
        <div className="flex rounded-md border border-border p-0.5" role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              aria-pressed={p === period}
              className={cn(
                "cursor-pointer rounded px-3 py-1 text-sm transition-colors",
                p === period ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {p} days
            </button>
          ))}
        </div>
      </div>

      {funnelQuery.isError ? (
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-destructive">
          Could not load the funnel: {funnelQuery.error.message}
        </p>
      ) : rulesQuery.isError ? (
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-destructive">
          Could not load the rules: {rulesQuery.error.message}
        </p>
      ) : funnel && rules.length ? (
        <div className={cn("transition-opacity delay-150 duration-200", funnelQuery.isFetching && "opacity-60")}>
          <BotFunnelView
            funnel={funnel}
            saved={saved}
            lastToggle={lastToggle}
            onToggle={toggle}
            periodLabel={`${ddmmyyyy(from)} – ${ddmmyyyy(to)}`}
          />
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-lg border border-border bg-card">
          <Spinner />
        </div>
      )}

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-1 font-semibold">Rules</h3>
        <p className="mb-2 text-xs text-muted-foreground">
          A click is a bot if any rule that is on matches it; in the funnel above the rules apply in this order.
        </p>
        {rulesQuery.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="divide-y divide-border">{rules.map(ruleSettings)}</div>
        )}
      </section>

      {changed.length ? (
        <div
          role="status"
          aria-live="polite"
          className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 shadow-lg"
        >
          <div className="min-w-0 space-y-0.5 text-sm">
            {lastRule && lastNet ? (
              <p className="font-medium">
                <span className="text-chart-1">●</span> {lastRule.label} {lastRule.enabled ? "on" : "off"} · clean{" "}
                {signed(lastNet.clicks, int.format)} clicks ·{" "}
                <span className={lastNet.sales < 0 || lastNet.revenue < 0 ? "signal-text-negative" : undefined}>
                  {signed(lastNet.sales, int.format)} {Math.abs(lastNet.sales) === 1 ? "sale" : "sales"} ·{" "}
                  {signed(lastNet.revenue, euro.format)}
                </span>
              </p>
            ) : null}
            <p className="text-muted-foreground">
              {changed.length} unsaved {changed.length === 1 ? "change" : "changes"}
              {funnel && savedFunnel
                ? ` · vs saved: clean ${int.format(savedFunnel.clean.clicks)} → ${int.format(funnel.clean.clicks)} clicks · sales ${savedFunnel.clean.sales} → ${funnel.clean.sales} · ${euro.format(savedFunnel.clean.revenue)} → ${euro.format(funnel.clean.revenue)}`
                : ""}
            </p>
            {listEdited ? (
              <p className="text-xs text-muted-foreground">
                User-agent / ISP list edits apply from the next hourly refresh; the funnel cannot preview them.
              </p>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={discard} disabled={save.isPending}>
              Discard
            </Button>
            <Button type="button" onClick={onSave} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
